import { createReducer } from './utils';
import { translate as t } from '@/core/i18n/translate';
import { appendScopeToApiUrl } from '@/lib/queryClient';
import {
  CREATE_ANNUAL_PLAN_REQUEST,
  CREATE_ANNUAL_PLAN_SUCCESS,
  CREATE_ANNUAL_PLAN_FAILURE,
  GET_ANNUAL_PLANS_REQUEST,
  GET_ANNUAL_PLANS_SUCCESS,
  GET_ANNUAL_PLANS_FAILURE,
  GET_ANNUAL_PLAN_REQUEST,
  GET_ANNUAL_PLAN_SUCCESS,
  GET_ANNUAL_PLAN_FAILURE,
  GET_FOCUSES_REQUEST,
  GET_FOCUSES_SUCCESS,
  GET_FOCUSES_FAILURE,
  CREATE_FOCUS_REQUEST,
  CREATE_FOCUS_SUCCESS,
  CREATE_FOCUS_FAILURE,
  GET_EXTERNAL_DATA_REQUEST,
  GET_EXTERNAL_DATA_SUCCESS,
  GET_EXTERNAL_DATA_FAILURE,
  GET_REQUIREMENTS_REQUEST,
  GET_REQUIREMENTS_SUCCESS,
  GET_REQUIREMENTS_FAILURE,
  APPROVE_DEFINITIONS_REQUEST,
  APPROVE_DEFINITIONS_SUCCESS,
  APPROVE_DEFINITIONS_FAILURE,
  RESET_ANNUAL_PLAN_V2_STATE,
  SNACK_SET,
} from './constants';
import { AnnualPlan, AnnualPlanVersion, APFocus, AnnualPlanObjective } from '@shared/schema';
import { Action, ThunkAPI } from '../types/state';

interface AnnualPlanV2State {
  annualPlans: AnnualPlan[];
  currentPlan: AnnualPlan | null;
  currentVersion: AnnualPlanVersion | null;
  focuses: APFocus[];
  objectives: AnnualPlanObjective[];
  requirements: any[];
  externalData: any;
  loading: {
    plans: boolean;
    focuses: boolean;
    objectives: boolean;
    requirements: boolean;
    externalData: boolean;
  };
  errorMessage: string | null;
}

const defaultState: AnnualPlanV2State = {
  annualPlans: [],
  currentPlan: null,
  currentVersion: null,
  focuses: [],
  objectives: [],
  requirements: [],
  externalData: null,
  loading: {
    plans: false,
    focuses: false,
    objectives: false,
    activities: false,
    requirements: false,
    externalData: false,
  },
  errorMessage: null,
};

export const reducer = createReducer(defaultState, {
  [RESET_ANNUAL_PLAN_V2_STATE]: () => {
    return defaultState;
  },
  [GET_ANNUAL_PLANS_REQUEST]: (state: AnnualPlanV2State) => ({
    ...state,
    loading: { ...state.loading, plans: true },
  }),
  [GET_ANNUAL_PLANS_SUCCESS]: (state: AnnualPlanV2State, action: Action<unknown>) => ({
    ...state,
    loading: { ...state.loading, plans: false },
    annualPlans: action.payload as AnnualPlan[],
    errorMessage: null,
  }),
  [GET_ANNUAL_PLANS_FAILURE]: (state: AnnualPlanV2State, action: Action<unknown>) => ({
    ...state,
    loading: { ...state.loading, plans: false },
    errorMessage: String(action.payload),
  }),

  [GET_ANNUAL_PLAN_REQUEST]: (state: AnnualPlanV2State) => ({
    ...state,
    loading: { ...state.loading, plans: true },
  }),
  [GET_ANNUAL_PLAN_SUCCESS]: (state: AnnualPlanV2State, action: Action<unknown>) => ({
    ...state,
    loading: { ...state.loading, plans: false },
    currentPlan: action.payload as AnnualPlan,
    errorMessage: null,
  }),
  [GET_ANNUAL_PLAN_FAILURE]: (state: AnnualPlanV2State, action: Action<unknown>) => ({
    ...state,
    loading: { ...state.loading, plans: false },
    errorMessage: String(action.payload),
  }),

  [CREATE_ANNUAL_PLAN_REQUEST]: (state: AnnualPlanV2State) => ({
    ...state,
    loading: { ...state.loading, plans: true },
  }),
  [CREATE_ANNUAL_PLAN_SUCCESS]: (state: AnnualPlanV2State, action: Action<unknown>) => {
    const { plan, version } = action.payload as { plan: AnnualPlan; version: AnnualPlanVersion };
    return {
      ...state,
      loading: { ...state.loading, plans: false },
      annualPlans: [...state.annualPlans, plan],
      currentPlan: plan,
      currentVersion: version,
      errorMessage: null,
    };
  },
  [CREATE_ANNUAL_PLAN_FAILURE]: (state: AnnualPlanV2State, action: Action<unknown>) => ({
    ...state,
    loading: { ...state.loading, plans: false },
    errorMessage: String(action.payload),
  }),

  [GET_FOCUSES_REQUEST]: (state: AnnualPlanV2State) => ({
    ...state,
    loading: { ...state.loading, focuses: true },
  }),
  [GET_FOCUSES_SUCCESS]: (state: AnnualPlanV2State, action: Action<unknown>) => ({
    ...state,
    loading: { ...state.loading, focuses: false },
    focuses: action.payload as APFocus[],
    errorMessage: null,
  }),
  [GET_FOCUSES_FAILURE]: (state: AnnualPlanV2State, action: Action<unknown>) => ({
    ...state,
    loading: { ...state.loading, focuses: false },
    errorMessage: String(action.payload),
  }),

  [CREATE_FOCUS_REQUEST]: (state: AnnualPlanV2State) => ({
    ...state,
    loading: { ...state.loading, focuses: true },
  }),
  [CREATE_FOCUS_SUCCESS]: (state: AnnualPlanV2State, action: Action<unknown>) => ({
    ...state,
    loading: { ...state.loading, focuses: false },
    focuses: [...state.focuses, action.payload as APFocus],
    errorMessage: null,
  }),
  [CREATE_FOCUS_FAILURE]: (state: AnnualPlanV2State, action: Action<unknown>) => ({
    ...state,
    loading: { ...state.loading, focuses: false },
    errorMessage: String(action.payload),
  }),

  [GET_EXTERNAL_DATA_REQUEST]: (state: AnnualPlanV2State) => ({
    ...state,
    loading: { ...state.loading, externalData: true },
  }),
  [GET_EXTERNAL_DATA_SUCCESS]: (state: AnnualPlanV2State, action: Action<unknown>) => ({
    ...state,
    loading: { ...state.loading, externalData: false },
    externalData: action.payload,
    errorMessage: null,
  }),
  [GET_EXTERNAL_DATA_FAILURE]: (state: AnnualPlanV2State, action: Action<unknown>) => ({
    ...state,
    loading: { ...state.loading, externalData: false },
    errorMessage: String(action.payload),
  }),

  [GET_REQUIREMENTS_REQUEST]: (state: AnnualPlanV2State) => ({
    ...state,
    loading: { ...state.loading, requirements: true },
  }),
  [GET_REQUIREMENTS_SUCCESS]: (state: AnnualPlanV2State, action: Action<unknown>) => ({
    ...state,
    loading: { ...state.loading, requirements: false },
    requirements: action.payload as any[],
    errorMessage: null,
  }),
  [GET_REQUIREMENTS_FAILURE]: (state: AnnualPlanV2State, action: Action<unknown>) => ({
    ...state,
    loading: { ...state.loading, requirements: false },
    errorMessage: String(action.payload),
  }),

  [APPROVE_DEFINITIONS_REQUEST]: (state: AnnualPlanV2State) => ({
    ...state,
    loading: { ...state.loading, plans: true },
  }),
  [APPROVE_DEFINITIONS_SUCCESS]: (state: AnnualPlanV2State, action: Action<unknown>) => {
    const updatedPlan = action.payload as AnnualPlan;
    return {
      ...state,
      loading: { ...state.loading, plans: false },
      currentPlan: updatedPlan,
      annualPlans: state.annualPlans.map((plan) =>
        plan.id === updatedPlan.id ? updatedPlan : plan,
      ),
      errorMessage: null,
    };
  },
  [APPROVE_DEFINITIONS_FAILURE]: (state: AnnualPlanV2State, action: Action<unknown>) => ({
    ...state,
    loading: { ...state.loading, plans: false },
    errorMessage: String(action.payload),
  }),
});

export function getAnnualPlanById(id: number) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: GET_ANNUAL_PLAN_REQUEST });
    try {
      const plan = await dataSource.getAnnualPlanById(id);
      dispatch({ type: GET_ANNUAL_PLAN_SUCCESS, payload: plan });
    } catch (error) {
      console.error({ error });
      dispatch({ type: GET_ANNUAL_PLAN_FAILURE, payload: error });
    }
  };
}

export function getExternalData() {
  return async (
    dispatch: ThunkAPI['dispatch'],
    getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: GET_EXTERNAL_DATA_REQUEST });
    try {
      const data = await dataSource.getExternalData();
      dispatch({ type: GET_EXTERNAL_DATA_SUCCESS, payload: data });
    } catch (error) {
      console.error({ error });
      dispatch({ type: GET_EXTERNAL_DATA_FAILURE, payload: error });
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: {
            open: true,
            message: t('apiFeedback.externalData.fetchError'),
            severity: 'error',
          },
        },
      });
      throw error;
    }
  };
}

export function resetState() {
  return (dispatch: ThunkAPI['dispatch']) => {
    dispatch({ type: 'RESET_ANNUAL_PLAN_V2_STATE' });
  };
}

export function approveDefinitions(
  planId: number,
  approvalData: {
    decision: string;
    comments?: string;
    additionalComments?: string;
    signature?: string;
    notifiedUsers?: string[];
  },
) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: APPROVE_DEFINITIONS_REQUEST });
    try {
      const response = await fetch(
        appendScopeToApiUrl(`/api/v2/annual-plans/${planId}/approve-definitions`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(approvalData),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to approve plan definitions');
      }

      const result = await response.json();

      const updatedPlan = await dataSource.getAnnualPlanById(planId);

      dispatch({ type: APPROVE_DEFINITIONS_SUCCESS, payload: updatedPlan });

      if (approvalData.decision === 'aprobar') {
        dispatch({
          type: SNACK_SET,
          payload: {
            snack: {
              open: true,
              message: 'Plan aprobado exitosamente y movido al segundo término',
              severity: 'success',
            },
          },
        });
      } else {
        dispatch({
          type: SNACK_SET,
          payload: { snack: { open: true, message: 'Plan rechazado', type: 'info' } },
        });
      }

      return result;
    } catch (error) {
      console.error({ error });
      dispatch({ type: APPROVE_DEFINITIONS_FAILURE, payload: error });
      dispatch({
        type: SNACK_SET,
        payload: {
          snack: { open: true, message: 'Error al procesar la aprobación', severity: 'error' },
        },
      });
      throw error;
    }
  };
}
